import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api, type AuthResponse } from '@/lib/api'

interface User {
  id: string
  name: string
  email: string
  division: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  refreshToken: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
  const storedToken = localStorage.getItem('token')
  const storedRefreshToken = localStorage.getItem('refreshToken')
  const storedUser = localStorage.getItem('user')

  if (storedToken && storedUser) {
      setToken(storedToken)
      if (storedRefreshToken) setRefreshToken(storedRefreshToken)
      setUser(JSON.parse(storedUser))
  }
  setIsLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    try {
      const response: AuthResponse = await api.login({ email, password })
      setToken(response.token)
      setRefreshToken(response.refreshToken)
      setUser(response.user)
      localStorage.setItem('token', response.token)
      localStorage.setItem('refreshToken', response.refreshToken)
      localStorage.setItem('user', JSON.stringify(response.user))
    } catch (error) {
      throw error
    }
    }

  const logout = () => {
  setToken(null)
  setRefreshToken(null)
  setUser(null)
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('user')
  }

  return (
  <AuthContext.Provider value={{ user, token, refreshToken, login, logout, isLoading }}>
      {children}
  </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
